// partners/adminView/pages/A4_AdminPartnersList.jsx
// Partners list with search, tier, and type filters.

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useGetAdminPartnersQuery } from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { formatUSD } from "../formatMoney";
import { capturePartnerEvent } from "../../../utils/analytics";

const TIER_BADGE = {
  bronze:   "bg-amber-800/30 text-amber-400",
  silver:   "bg-gray-400/20 text-gray-300",
  gold:     "bg-yellow-500/20 text-yellow-400",
  platinum: "bg-cyan-500/20 text-cyan-400",
  diamond:  "bg-purple-500/20 text-purple-400",
};

export default function AdminPartnersList() {
  const [search,     setSearch]     = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const { data, isLoading } = useGetAdminPartnersQuery({
    search,
    tier: tierFilter,
    type: typeFilter,
  });

  const partners = data?.results || data || [];
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_partners_viewed");
    }
  }, [isLoading]);

  const trackFilter = (overrides) => {
    capturePartnerEvent("partner_admin_partners_filtered", {
      search: overrides.search ?? search,
      tier: overrides.tier ?? tierFilter,
      type: overrides.type ?? typeFilter,
    });
  };

  return (
    <AdminLayout>
      <h1 className="font-poppins text-2xl font-bold mb-6">Partners</h1>

      <div className="flex gap-3 mb-6 flex-wrap">
        <input
          value={search}
          onChange={(e) => {
            const val = e.target.value;
            setSearch(val);
            trackFilter({ search: val });
          }}
          placeholder="Search by name or code…"
          className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf px-3 py-2 text-sm text-mnf-text-primary focus:outline-none focus:border-mnf-cyan w-60"
        />
        <select
          value={tierFilter}
          onChange={(e) => {
            const val = e.target.value;
            setTierFilter(val);
            trackFilter({ tier: val });
          }}
          className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
        >
          <option value="">All tiers</option>
          {["bronze", "silver", "gold", "platinum", "diamond"].map((t) => (
            <option key={t} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            const val = e.target.value;
            setTypeFilter(val);
            trackFilter({ type: val });
          }}
          className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
        >
          <option value="">All types</option>
          <option value="ecosystem">Ecosystem</option>
          <option value="creative_ambassador">Ambassador</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-mnf-text-muted text-sm">Loading...</div>
      ) : (
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-mnf-dark-3 text-mnf-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left">Partner</th>
                <th className="px-5 py-3 text-left">Code</th>
                <th className="px-5 py-3 text-left">Tier</th>
                <th className="px-5 py-3 text-left">Conversions</th>
                <th className="px-5 py-3 text-left">Earnings</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-mnf-text-muted">
                    No partners found.
                  </td>
                </tr>
              )}
              {partners.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-white/[0.04] hover:bg-mnf-dark-3 transition-colors"
                >
                  <td className="px-5 py-3">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-mnf-text-muted">
                      {p.partner_type === "creative_ambassador" ? "Ambassador" : "Ecosystem"}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-mnf-cyan text-xs">{p.referral_code}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-poppins font-semibold px-2.5 py-1 rounded-full capitalize ${
                        TIER_BADGE[p.tier] || "bg-white/10 text-white"
                      }`}
                    >
                      {p.tier}
                    </span>
                  </td>
                  <td className="px-5 py-3">{p.total_conversions?.toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3">{formatUSD(p.total_earnings)}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-xs font-semibold ${
                        p.is_active ? "text-mnf-success" : "text-mnf-error"
                      }`}
                    >
                      {p.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <Link
                      to={`/partners/admin/partners/${p.id}`}
                      className="text-xs text-mnf-cyan hover:underline"
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
